import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import {
  useCreateClaimMutation,
  useGetClaimLookupsQuery,
} from "../../api/claimsApi";
import { BaseFormPage } from "../../components/common/BaseForm";
import { useFormSubmission } from "../../hooks/useFormSubmission";
import ClaimFormLayout from "./components/ClaimFormLayout";
import { PendingFile } from "./components/ClaimPendingFilesSection";
import {
  claimValidationSchema,
  getInitialClaimFormValues,
  ClaimFormValues,
  computeTotalEstimate,
  parseEstimate,
} from "./validation/claimFormValidation";
import { CreateClaimDto } from "../../types/claim";

const ClaimCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [createClaim] = useCreateClaimMutation();
  const { data: lookups } = useGetClaimLookupsQuery();
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  const {
    isSubmitting,
    submitError,
    successMessage,
    showValidationErrors,
    handleSubmit,
  } = useFormSubmission({
    successMessage: "Claim created successfully!",
    onSuccess: () => {
      setTimeout(() => navigate("/claims"), 1500);
    },
  });

  const formik = useFormik<ClaimFormValues>({
    initialValues: getInitialClaimFormValues(),
    validationSchema: claimValidationSchema,
    onSubmit: async (values) => {
      await handleSubmit(async () => {
        const dto: CreateClaimDto = {
          dateOfLoss: values.dateOfLoss,
          city: values.city || null,
          state: values.state || null,
          claimCompanyId: values.claimCompanyId,
          claimCompanyName: values.claimCompanyName,
          assignedToUserId: values.assignedToUserId,
          driverEmployeeId: values.driverEmployeeId,
          vehicleId: values.vehicleId,
          trailerId: values.trailerId,
          description: values.description || null,
          claimNumber: values.claimNumber || null,
          note: values.note || null,
          adjusterName: values.adjusterName || null,
          adjusterPhone: values.adjusterPhone || null,
          adjusterEmail: values.adjusterEmail || null,
          autoLB: values.autoLB,
          policeReportDescription: values.policeReportDescription || null,
          truckEstimate: parseEstimate(values.truckEstimate),
          trailerEstimate: parseEstimate(values.trailerEstimate),
          totalEstimate: computeTotalEstimate(
            values.truckEstimate,
            values.trailerEstimate,
            values.legacyTotalEstimate
          ),
          status: values.status,
          driverCharged: values.driverCharged,
        };

        const newFiles = pendingFiles.filter((pf) => pf.file);
        const files = newFiles.map((pf) => pf.file!);
        const fileCategories = newFiles.map((pf) => pf.documentCategory || "");

        return await createClaim({ dto, files, fileCategories }).unwrap();
      });
    },
  });

  return (
    <BaseFormPage
      title="Create Claim"
      subtitle="Add a new claim to the board"
      onBack={() => navigate("/claims")}
      formik={formik}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={submitError}
      showValidationErrors={showValidationErrors}
      submitButtonText="Create Claim"
      maxWidth="lg"
    >
      <ClaimFormLayout
        mode="create"
        availableCompanies={lookups?.availableCompanies || []}
        availableDrivers={lookups?.availableDrivers || []}
        availableSafetyUsers={lookups?.availableSafetyUsers || []}
        availableTrucks={lookups?.availableTrucks || []}
        availableTrailers={lookups?.availableTrailers || []}
        pendingFiles={pendingFiles}
        onPendingFilesChange={setPendingFiles}
      />
    </BaseFormPage>
  );
};

export default ClaimCreatePage;
